import { formatPrice, getAssetUrl } from "../utils"
import { MapProperty } from "./types"
import { DEFAULT_NO_DATES_LABEL, calendarFreshForLabel, rateUnbookable } from "../property-card/no-dates-label"
import { readCardTagsConfig, collectCardTags, cardTagPillStyleAttr } from "../property-card/label-style"
import type { CardTagsConfig } from "../../widgets/explorer/labels/schema"

/**
 * Resolves the infowindow icon settings to concrete URLs (#567, WP parity):
 * empty/unset falls back to the bundled defaults served from the widget
 * asset base (assets/map-icons/*), 'none' hides the icon — mirroring WP,
 * where clearing the field restores the plugin-bundled default.
 */
export function resolveInfowindowIcons(map?: { infowindowLocationsIcon?: string; infowindowTypesIcon?: string }) {
  const resolve = (value: string | undefined, fallback: string) =>
    value === 'none' ? undefined : (value || getAssetUrl(fallback))
  return {
    locationsIcon: resolve(map?.infowindowLocationsIcon, 'map-icons/locations-icon.svg'),
    typesIcon: resolve(map?.infowindowTypesIcon, 'map-icons/types-icon.svg'),
  }
}

/**
 * #1188: true when the pin and popup must not advertise a price — Central's
 * calendar-derived low says nothing is bookable (`rateUnbookable`) on a
 * calendar it vouches for (`calendarFreshForLabel`): the same two gates as
 * the explorer card (#1187/#1189) and the saved panel / standalone PriceRow
 * (#1199). Read off the property object so every caller gets the verdict
 * without a change of its own. A payload that never carried `lowestPrice`
 * (the property-map widget's property-details shape) is not a signal and
 * keeps its price. The parameter is the structural subset the verdict reads
 * (#1310: stack-group members are StackableProperty, not full MapProperty);
 * every existing MapProperty caller satisfies it unchanged.
 */
export function mapPropertyUnbookable(
  property: Pick<MapProperty, 'lowestPrice' | 'bookableNights' | 'calendarStale'>,
): boolean {
  return rateUnbookable(property.lowestPrice) && calendarFreshForLabel(property)
}

/**
 * The optional trailing label argument of the two helpers below: omitted or
 * blank = the canonical wording; `null` = the operator opted out
 * (`priceDisplay.showNoDatesLabel: false`) and the legacy price pin / popup
 * stay. The explorer maps pass their parsed `priceDisplay` verdict
 * (`noDatesLabel(options)`); other callers omit it.
 */
function resolveNoDatesLabel(label: string | null): string | null {
  if (label === null) return null
  const text = label.trim()
  return text.length > 0 ? text : DEFAULT_NO_DATES_LABEL
}

// Operator text into string HTML (the popup is innerHTML, not JSX).
function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

export function getMarkerBadgeHtml(
  property: MapProperty,
  showPrice: boolean,
  noDatesLabel: string | null = DEFAULT_NO_DATES_LABEL,
): string {
  // #1188: an unbookable listing gets the neutral house pin instead of its
  // base rate; the wording itself lives in the popup (a pin is too small).
  const unbookable = resolveNoDatesLabel(noDatesLabel) !== null && mapPropertyUnbookable(property)
  if (showPrice && !unbookable && property.price > 0) {
    // Feed currency, not a hardcoded $ (#994). Marker HTML is client-built
    // (Leaflet/Google divIcon after mount), so visitor-locale Intl is safe here.
    return `<div class="hfse-map-badge hfse-map-badge-price">${formatPrice(property.price, property.currency || 'USD')}</div>`
  }

  // Default house SVG icon
  return `<div class="hfse-map-badge hfse-map-badge-icon"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></div>`
}

export function getPopupHtml(
  property: MapProperty,
  imageUrl: string,
  pdpUrl: string,
  icons?: { locationsIcon?: string; typesIcon?: string },
  noDatesLabel: string | null = DEFAULT_NO_DATES_LABEL,
  cardTags?: CardTagsConfig | null,
): string {
  const label = resolveNoDatesLabel(noDatesLabel)
  const priceHtml = label !== null && mapPropertyUnbookable(property)
    // #1188: the label where "Starting at" was — same class as the card's
    // (.hfse-property-price-unavailable, explorer-card.css), opacity only.
    ? `<div class="hfse-property-infowindow-price"><span class="hfse-property-price-unavailable">${escapeText(label)}</span></div>`
    : property.price > 0
      ? `<div class="hfse-property-infowindow-price"><span class="hfse-property-price-total">Starting at <strong>${formatPrice(property.price, property.currency || 'USD')}</strong></span></div>`
      : ''
  // Infowindow term icons (#567, WP: map_infowindow_locations_icon /
  // map_infowindow_types_icon) — image URLs, empty hides the icon.
  const iconImg = (src?: string) =>
    src ? `<img class="hfse-infowindow-term-icon" src="${src}" alt="" aria-hidden="true">` : ''

  // Card tags (#1819, the #1812 explorer-card port on the popup): the toolkit
  // rewrites the REST `types` slot, so its popup's type line shows the same
  // tag label(s) as the card. Same resolver as the card (priority order,
  // singular label, cap), same inline pill look; text mode = the slash-joined
  // line; a home with none of the listed terms keeps its type unless keepType
  // is off, which empties the slot. The type icon belongs to the type and is
  // not drawn over the tags, exactly as the card's tag slot carries no icon.
  // The argument is the raw `card.cardTags` group; the toolkit off gate
  // (`readCardTagsConfig`: enabled AND at least one term) runs here, so an
  // omitted / off / empty group = today's type span, verbatim.
  const tagsCfg = readCardTagsConfig({ cardTags })
  const tags = tagsCfg ? collectCardTags(property, tagsCfg) : []
  const typeHtml = tagsCfg && tags.length > 0
    ? (tagsCfg.pills === false
      ? `<span>${escapeText(tags.map((t) => t.text).join(' / '))}</span>`
      : `<span class="hfse-infowindow-tags">${tags.map((t) => `<span class="hfse-card-tag" style="${cardTagPillStyleAttr(tagsCfg)}">${escapeText(t.text)}</span>`).join('')}</span>`)
    : (tagsCfg && tagsCfg.keepType === false
      ? ''
      : `<span>${iconImg(icons?.typesIcon)}${property.propertyType || ''}</span>`)

  return `<div class="hfse-map-tooltip">
    <div class="hfse-map-tooltip-close-btn">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="12" height="12"><path d="m6 6 20 20M26 6 6 26" stroke="white" stroke-width="3" fill="none"/></svg>
    </div>
    <div class="hfse-map-tooltip-content">
      <a class="hk-card hk-card-small hk-card-default hk-display-block hk-text-decoration-none" href="${pdpUrl}" target="_blank">
        ${imageUrl ? `<div class="hfse-property-infowindow-header" style="background-image:url(${imageUrl});background-size:cover;background-position:center">
          <div class="hk-overlay hk-position-cover hfse-property-infowindow-header-overlay"></div>
        </div>` : ''}
        <div class="hk-card-body hfse-property-infowindow-body">
          <div class="hk-h5 hk-margin-small-bottom hfse-property-title">${property.title}</div>
          <div class="hk-text-default hk-margin-remove-top hk-margin-small-bottom hfse-property-subtitle">${iconImg(icons?.locationsIcon)}${property.location?.label || ''}</div>
          ${priceHtml}
          <div class="hfse-property-infowindow-meta">
            ${typeHtml}
            <span>${property.features.guests} guests · ${property.features.beds || 'Studio'} beds · ${property.features.baths} baths</span>
          </div>
        </div>
      </a>
    </div>
  </div>`
}
